<template>
	<div class="dropdown-menu show" :class="{ 'dropdown-menu-right': useRight }" :style="computedStyle" v-if="controller.menuShow.value" ref="dropdownRef">
		<DropdownMenu :items="controller.menuItems.value"></DropdownMenu>
	</div>
</template>

<script>
import { onMounted, onUnmounted, ref, computed, watch, nextTick } from 'vue';
import DropdownMenu from '@/components/DropdownMenu.vue';

export default {
	components: {
		DropdownMenu
	},

	props: {
		style: {
			type: String,
			default: 'right: 0px; left: unset;'
		},
		controller: {
			type: Object,
			default: () => ({})
		}
	},

	setup(props) {
		const dropdownRef = ref(null);
		const adjustedStyle = ref(props.style);
		const useRight = ref(false);
		
		// Обработчик hover для подменю (объявляем в начале, чтобы использовать в onMounted и onUnmounted)
		const handleSubmenuHover = (e) => {
			if (dropdownRef.value && dropdownRef.value.contains(e.target)) {
				const submenu = e.target.closest('.dropdown-submenu');
				if (submenu) {
					nextTick(() => {
						adjustSubmenuPosition(submenu);
					});
				}
			}
		};

		const closePopup = () => {
			props.controller.menuShow.value = false;
		}

		const adjustPosition = () => {
			if (!dropdownRef.value) return;

			const dropdown = dropdownRef.value;
			
			// Парсим оригинальный стиль
			const topMatch = props.style.match(/top:\s*([\d.]+)px/);
			const leftMatch = props.style.match(/left:\s*([\d.]+)px/);
			const rightMatch = props.style.match(/right:\s*([\d.]+)px/);
			const widthMatch = props.style.match(/width:\s*([\d.]+px)/);
			
			let top = topMatch ? parseFloat(topMatch[1]) : 0;
			let left = leftMatch ? parseFloat(leftMatch[1]) : null;
			let right = rightMatch ? parseFloat(rightMatch[1]) : null;
			const originalWidth = widthMatch ? widthMatch[1] : '500px';
			const dropdownWidth = parseInt(originalWidth) || 500;
			
			// Получаем позицию родительского элемента
			const parentElement = dropdown.parentElement;
			if (!parentElement) return;
			
			const parentRect = parentElement.getBoundingClientRect();
			const viewportWidth = window.innerWidth;
			const padding = 10;
			
			// Вычисляем абсолютную позицию на экране при использовании left
			let absoluteLeft = 0;
			if (left !== null) {
				absoluteLeft = parentRect.left + left;
			} else if (right !== null) {
				absoluteLeft = parentRect.right - right - dropdownWidth;
			} else {
				absoluteLeft = parentRect.left;
			}
			
			// Проверяем ЗАРАНЕЕ - если выходит за правую границу, переключаем на right: 0
			let useLeftValue = left;
			
			if (absoluteLeft + dropdownWidth > viewportWidth - padding) {
				// Меню выходит за правую границу - переключаем на right: 0 (показываем слева)
				useLeftValue = null;
				useRight.value = true;
			} else {
				useRight.value = false;
			}
			
			// Формируем итоговый стиль
			let styleString = `position: absolute; top: ${top}px; `;
			if (useLeftValue !== null && !useRight.value) {
				styleString += `left: ${useLeftValue}px; `;
				styleString += 'right: auto; ';
			} else {
				styleString += 'right: 0px; ';
				styleString += 'left: auto; ';
			}
			styleString += `width: ${originalWidth}; max-width: calc(100vw - 20px); `;
			
			adjustedStyle.value = styleString;
		}

		const computedStyle = computed(() => {
			return adjustedStyle.value;
		});

		watch(() => props.controller.menuShow.value, (isOpen) => {
			if (isOpen) {
				// Сначала устанавливаем базовый стиль
				adjustedStyle.value = props.style;
				
				// Затем корректируем позицию после рендера - несколько раз для надежности
				nextTick(() => {
					if (dropdownRef.value) {
						adjustPosition();
					}
					// Еще раз после полного рендера для финальной проверки
					nextTick(() => {
						if (dropdownRef.value) {
							const rect = dropdownRef.value.getBoundingClientRect();
							if (rect.right > window.innerWidth - 10) {
								// Все еще выходит - переключаем на right: 0
								const topMatch = props.style.match(/top:\s*([\d.]+)px/);
								const top = topMatch ? parseFloat(topMatch[1]) : 0;
								const widthMatch = props.style.match(/width:\s*([\d.]+px)/);
								const originalWidth = widthMatch ? widthMatch[1] : '500px';
								useRight.value = true;
								adjustedStyle.value = `position: absolute; top: ${top}px; right: 0px; left: auto; width: ${originalWidth}; max-width: calc(100vw - 20px); `;
							}
							// Настраиваем позиционирование подменю
							const submenus = dropdownRef.value.querySelectorAll('.dropdown-submenu');
							submenus.forEach((submenu) => {
								adjustSubmenuPosition(submenu);
							});
						}
					});
				});
			}
		});

		const adjustSubmenuPosition = (submenuElement) => {
			if (!submenuElement) return;
			
			const submenuMenu = submenuElement.querySelector('.dropdown-menu');
			if (!submenuMenu) return;
			
			// Временно показываем меню для измерения
			const wasVisible = submenuMenu.style.display !== 'none';
			if (!wasVisible) {
				submenuMenu.style.display = 'block';
				submenuMenu.style.visibility = 'hidden';
			}
			
			const viewportWidth = window.innerWidth;
			const padding = 10;
			const submenuRect = submenuElement.getBoundingClientRect();
			const submenuMenuRect = submenuMenu.getBoundingClientRect();
			
			// Проверяем доступное место справа и слева
			const spaceRight = viewportWidth - submenuRect.right - padding;
			const spaceLeft = submenuRect.left - padding;
			const submenuWidth = submenuMenuRect.width || 500;
			
			// Если справа мало места, но слева достаточно - показываем слева
			if (spaceRight < submenuWidth && spaceLeft >= submenuWidth) {
				submenuElement.classList.add('dropdown-submenu-left');
			} else {
				// Показываем справа (по умолчанию)
				submenuElement.classList.remove('dropdown-submenu-left');
			}
			
			// Восстанавливаем видимость
			if (!wasVisible) {
				submenuMenu.style.display = '';
				submenuMenu.style.visibility = '';
			}
		};

		const handleResize = () => {
			if (props.controller.menuShow.value) {
				adjustPosition();
				nextTick(() => {
					if (dropdownRef.value) {
						const submenus = dropdownRef.value.querySelectorAll('.dropdown-submenu');
						submenus.forEach((submenu) => {
							adjustSubmenuPosition(submenu);
						});
					}
				});
			}
		};

		onMounted(() => {
			document.addEventListener("click", closePopup);
			window.addEventListener("resize", handleResize);
			// Добавляем обработчик на document для делегирования hover на подменю
			document.addEventListener('mouseover', handleSubmenuHover, true);
		})

		onUnmounted(() => {
			document.removeEventListener("click", closePopup);
			window.removeEventListener("resize", handleResize);
			document.removeEventListener('mouseover', handleSubmenuHover, true);
		})

		return {
			dropdownRef,
			computedStyle,
			useRight
		}
	}
}
</script>

<style scoped>
.dropdown-menu {
	position: absolute;
	z-index: 1000;
}
</style>

<style>
/* Глобальный стиль - ограничиваем ширину через CSS, но разрешаем overflow для подменю */
.dropdown-menu.show {
	max-width: min(500px, calc(100vw - 20px)) !important;
	overflow: visible !important;
}

/* Если меню переключено на right - принудительно устанавливаем right */
.dropdown-menu.show.dropdown-menu-right {
	left: auto !important;
	right: 0 !important;
}

/* Подменю - убираем жесткое позиционирование, будет определяться через JS */
.dropdown-menu.show .dropdown-submenu {
	position: relative;
}

.dropdown-menu.show .dropdown-submenu > .dropdown-menu {
	max-width: min(500px, calc(100vw - 40px)) !important;
	position: absolute;
	top: 0;
	margin-top: -0.5625rem;
	z-index: 1001;
	overflow: visible;
}

/* Подменю слева */
.dropdown-menu.show .dropdown-submenu.dropdown-submenu-left > .dropdown-menu {
	left: auto !important;
	right: 100% !important;
}

/* Подменю справа (по умолчанию) */
.dropdown-menu.show .dropdown-submenu:not(.dropdown-submenu-left) > .dropdown-menu {
	left: 100% !important;
	right: auto !important;
}
</style>